// Single source of truth for AEN's contact channels.
//
// Before this module the WhatsApp number, phone, and email lived as
// hardcoded strings sprinkled across ~17 files (Navbar, Footer, every
// page CTA, sticky FAB, etc.). When the number changed the team had to
// hunt with grep — easy to miss a locale-conditional component or a
// blog post. Now: change the constant once, every CTA updates.
//
// The `whatsappLink(message)` helper returns a properly-encoded
// `https://wa.me/...?text=...` URL. Pass plain English text — encoding
// is handled here. Unencoded `&` / `%` / spaces in the message would
// break the WhatsApp deep-link silently on iOS.

/** International dial-format WhatsApp number (no `+`, no spaces). */
export const WHATSAPP_NUMBER = '447728554750';

/** Display-format phone number (UK national, with spaces). */
export const PHONE_DISPLAY = '+44 1634 305 772';

/** Tel: URI form of the phone number (no spaces). */
export const PHONE_TEL = '+441634305772';

/** Primary inbound email. Routed to the team via Google Workspace. */
export const EMAIL = 'info@academyeducationnetwork.co.uk';

/** Postal address (no abbreviations, used in Footer + Contact + JSON-LD). */
export const POSTAL_ADDRESS = '124 City Road, Islington, London, EC1V 2NX';

/** Most pages prefill the WhatsApp message with this generic intro. */
export const DEFAULT_WHATSAPP_MESSAGE =
  "Hi, I'm interested in studying in the UK. Can you help me find the right course?";

/**
 * Build a wa.me deep-link with optional pre-filled message text.
 *
 *   whatsappLink()                     → https://wa.me/447728554750
 *   whatsappLink('Custom hello')       → https://wa.me/447728554750?text=Custom%20hello
 *   whatsappLink(undefined, false)     → https://wa.me/447728554750  (no default message)
 *
 * Returns a string suitable for an <a href>. Encodes the message with
 * encodeURIComponent so `&`, spaces, accents, emoji etc. all survive.
 */
export function whatsappLink(message, useDefault = true) {
  const text = message ?? (useDefault ? DEFAULT_WHATSAPP_MESSAGE : '');
  if (!text) return `https://wa.me/${WHATSAPP_NUMBER}`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
}
